import { PaginatedResource } from '../../typings/Paginated';
import { DashBoardParamList } from '../../typings/Params';
import { BaseService, ClientError, GenericPaginatedParameters, PartialBy, Unauthorized, UnexpectedError } from './base-service';
import { LPRServiceError, PlateData } from './license-plate-recognition';

export interface GetCamerasSummary {
    lprOnline: number;
    lprOffline: number;
    pinnedOnline: number;
    pinnedOffline: number;
    ptzOnline: number;
    ptzOffline: number;
    panoramicOnline: number;
    panoramicOffline: number;
    online: number;
    offline: number;
    stolen: number;
    createdToday: number;
    groupedCoordinatedCount: number;
}

export interface GetOccurrencesSummary {
    total: number;
    pendingOccurrences: number;
    inServiceOccurrences: number;
    onGoingOccurrences: number;
    concludedOccurrences: number;
    onFootTeamsTotal: number;
    fourWheelsTeamsTotal: number;
    twoWheelsTeamsTotal: number;
    activesTotal: number;
    onFootTeamsCurrent: number;
    fourWheelsTeamsCurrent: number;
    twoWheelsTeamsCurrent: number;
    onFootTeamsNameTotal: number;
    fourWheelsTeamsNameTotal: number;
    twoWheelsTeamsNameTotal: number;
    activesCurrent: number;
    posList: LatLng[];
}

export interface VehicleDashboardActingBody {
    id: number;
    name: string;
}

export interface VehicleDashboardActingBodyCommand {
    id: number;
    name: string;
}

export interface VehicleDashboard {
    id: number;
    name: string;
    category: string;
    actingBody: VehicleDashboardActingBody;
    actingBodyCommand: VehicleDashboardActingBodyCommand;
    isActive: boolean;
}


interface PaginatedAgentsParams {
    textFilter?: string;
    phone?: string;
    actingBodyId?: number;
    actingBodyUnitId?: number;
    offline?: boolean;
    online?: boolean;
    unitIds?: number[];
}


export interface DashboardAgent {
    id: number;
    name: string;
    warName: string | null;
    actingBodyId: number;
    phone: string;
    agentPosition: {
        positionUpdatedAt?: string;
        latitude: number;
        longitude: number;
        id: number;
    };
    unit: {
        id: number;
        unitName: {
            name: string;
            category: null;
        };
    };
}

interface PlateGroupSimplified {
    id: number;
    name: string;
}

export interface PlateGroup extends PlateGroupSimplified {
    description: string;
    activePlates: number;
    inactivePlates: number;
    createdAt: Date;
}

interface ExtendedPagination extends PaginatedResource<DashboardAgent> {
    activeAgents: number;
}

interface MonitoringPlatesPagination extends GenericPaginatedParameters {
    isActive?: boolean;
    groupsIds?: number[];
}

export type MonitoredPlatesDashboardDetection = {
    id: number;
    plate: string;
    imageUrl: string;
    cameraId?: string;
    eventReceiverIdentifier?: string;
    time: string;
    address?: string;
    latitude?: number;
    longitude?: number;
} & ({
    camera: {
        id: number;
        title: string;
        type: CameraType;
        address: string;
        latitude: string;
        longitude: string;
    };
    unitName: null;
} | {
    camera: null;
    unitName: {
        id: number;
        name: string;
        eventReceiverIdentifier: string;
    };
});

export interface MonitoredPlatesDashboardInfo {
    uniquePlatesQty: number;
    morningPlatesQty: number;
    afternoonPlatesQty: number;
    nightPlatesQty: number;
    earlyMorningPlatesQty: number;
    rankingPlates: [string, number][];
    lastCalculationTime?: number;
    detections: MonitoredPlatesDashboardDetection[];
}

class DashboardSummaryService extends BaseService {

    async getCamerasSummary(today: number): Promise<GetCamerasSummary> {
        const token = await this.getToken();
        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/cameras${this.encodeQueryParams({
            today
        })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getOccurrencesSummary(params: DashBoardParamList['DashboardOccurrences']): Promise<GetOccurrencesSummary> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/occurrences${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8')
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getAgentsSummary(params: PaginatedAgentsParams): Promise<ExtendedPagination> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/agents${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getVehiclesSummary(params: DashBoardParamList['DashboardVehicles']): Promise<VehicleDashboard[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/vehicles${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPlateGroups(params: Omit<GenericPaginatedParameters, 'actingBodyId'>): Promise<PaginatedResource<PlateGroup>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-groups${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async createPlateGroup(params: Omit<PlateGroup, 'id' | 'createdAt' | 'activePlates' | 'inactivePlates'>): Promise<PlateGroup> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-group`, {
            method: 'POST',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...params }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPlateGroup(id: number): Promise<PlateGroup> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-group/${id}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async updatePlateGroup(params: PartialBy<Omit<PlateGroup, 'createAt' | 'activePlates' | 'inactivePlates'>, 'id'>): Promise<PlateGroup> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-group/${params.id}`, {
            method: 'PUT',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
            body: JSON.stringify({ ...params }),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async deletePlateGroup(id: number): Promise<PlateGroup> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-group/${id}`, {
            method: 'DELETE',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        } else if (res.status >= 400 && res.status <= 499) {
            const resJson = await res.json();
            if (resJson.code == 'LPRServiceError') {
                throw new LPRServiceError(resJson.message);
            }
            throw new ClientError(resJson.code);
        } else if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getMonitoringPlates(params: MonitoringPlatesPagination): Promise<PaginatedResource<PlateData>> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plates${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getPlateGroupsSimplified(): Promise<PlateGroupSimplified[]> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/plate-groups-simplified`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }

    async getDashboardInfo(params: Omit<DashBoardParamList['DashboardPlateMonitoring'], 'selectedTab'>): Promise<MonitoredPlatesDashboardInfo> {
        const token = await this.getToken();

        const res = await fetch(`${this.centralEndpoint}/dashboard-summary/monitored-plates/dashboard-info${this.encodeQueryParams({ ...params })}`, {
            method: 'GET',
            headers: this.getHeaders(token, 'application/json;charset=utf-8'),
        });

        if (res.status == 401) {
            throw new Unauthorized();
        }

        if (res.status != 200) {
            throw new UnexpectedError();
        }

        return res.json();
    }
}

export const dashboardSummaryService = new DashboardSummaryService();
